import { useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Search, Banknote, AlertTriangle, CheckCircle2, Printer, Trophy, Ban, ScanLine, Camera } from "lucide-react";
import { toast } from "sonner";
import { printTicket } from "../components/PrintableTicket";
import QRScannerModal from "../components/QRScannerModal";

export default function Payouts() {
  const [ticketNum, setTicketNum] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paymentOk, setPaymentOk] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleScannedText = (text) => {
    setScannerOpen(false);
    // Accept either a raw ticket number or a URL like /t/{ticket_number}
    let tn = (text || "").trim();
    const m = tn.match(/\/t\/([^/?#]+)/);
    if (m) tn = decodeURIComponent(m[1]);
    tn = tn.toUpperCase();
    if (!tn) return toast.error("QR sin datos");
    setTicketNum(tn);
    setTimeout(() => searchByNumber(tn), 0);
  };

  const searchByNumber = async (tn) => {
    setLoading(true); setData(null); setPaymentOk(null);
    try {
      const { data } = await api.get(`/payouts/ticket/${encodeURIComponent(tn)}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const search = async () => {
    if (!ticketNum.trim()) return;
    await searchByNumber(ticketNum.trim());
  };

  const pay = async () => {
    if (!data?.ticket) return;
    setPaying(true);
    try {
      const { data: res } = await api.post(`/tickets/${data.ticket.id}/pay`);
      setPaymentOk(res);
      toast.success(`Pagado: RD$${res.amount.toFixed(2)}`);
      // refresh
      const { data: fresh } = await api.get(`/payouts/ticket/${encodeURIComponent(ticketNum.trim())}`);
      setData(fresh);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setPaying(false);
  };

  const t = data?.ticket;

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Pago de Premios"
        subtitle="Verificación y pago de tickets ganadores"
        testId="payouts-header"
      />

      <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Search panel */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-sm p-6 sticky top-24">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">
              Número de ticket
            </Label>
            <div className="relative mt-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={ticketNum}
                onChange={(e) => setTicketNum(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="BR01-20260419-000001"
                className="pl-9 h-12 text-base font-mono rounded-sm border-slate-300 focus:border-blue-600"
                autoFocus
                data-testid="payout-search-input"
              />
            </div>
            <Button
              onClick={search}
              disabled={loading || !ticketNum.trim()}
              className="w-full mt-3 h-12 bg-blue-600 hover:bg-blue-700 rounded-sm font-bold"
              data-testid="payout-search-btn"
            >
              {loading ? "Buscando…" : "Buscar"}
            </Button>
            <Button
              onClick={() => setScannerOpen(true)}
              variant="outline"
              className="w-full mt-2 h-12 rounded-sm border-slate-300 font-bold"
              data-testid="payout-scan-btn"
            >
              <Camera className="w-4 h-4 mr-2" /> Escanear QR
            </Button>

            <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-sm">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600 flex items-center gap-1">
                <ScanLine className="w-3 h-3" /> Escáner QR habilitado
              </div>
              <ul className="text-xs text-slate-700 mt-2 space-y-1 list-disc list-inside">
                <li>Escanea el QR del ticket con un lector USB.</li>
                <li>Verifica identidad del portador.</li>
                <li>Pulsa "Pagar" sólo cuando el premio esté confirmado.</li>
                <li>No se permite pagar dos veces el mismo ticket.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Result panel */}
        <div className="lg:col-span-3">
          {!data && (
            <div className="bg-white border border-dashed border-slate-300 rounded-sm p-16 text-center" data-testid="payout-empty">
              <Banknote className="w-10 h-10 text-slate-300 mx-auto" />
              <div className="text-sm text-slate-500 mt-3">
                Ingresa un número de ticket para comenzar.
              </div>
            </div>
          )}

          {data && t && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="payout-card">
              <StatusBanner data={data} t={t} />

              <div className="p-6 grid grid-cols-2 gap-y-3 gap-x-6 text-sm border-b border-slate-200">
                <Field label="Ticket" value={<span className="font-mono font-black text-lg">{t.ticket_number}</span>} />
                <Field label="Fecha emisión" value={<span className="font-mono">{new Date(t.created_at).toLocaleString("es-DO")}</span>} />
                <Field label="Sucursal" value={<span className="font-mono">{t.branch_code}</span>} />
                <Field label="Cajero" value={t.user_name} />
                <Field label="Total apostado" value={<span className="font-mono font-bold">RD${t.total_amount.toFixed(2)}</span>} />
                <Field label="Estado" value={<StatusBadge s={t.status} />} />
              </div>

              <div className="p-6">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">
                  Jugadas ({t.items.length})
                </div>
                <div className="bg-[#FFFDF0] border border-yellow-200 rounded-sm overflow-hidden">
                  <table className="w-full text-sm font-mono">
                    <thead className="bg-yellow-50">
                      <tr className="text-left">
                        <Th>Juego</Th><Th>Tipo</Th><Th>Números</Th><Th>Apuesta</Th><Th>Estado</Th><Th className="text-right">Premio</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.items.map((it) => (
                        <tr key={it.id} className="border-t border-yellow-100">
                          <td className="p-2 text-xs">{it.game_name}</td>
                          <td className="p-2 text-xs">{it.play_type_name}</td>
                          <td className="p-2 font-black text-slate-950">{it.display}</td>
                          <td className="p-2">RD${it.amount.toFixed(2)}</td>
                          <td className="p-2"><ItemStatus s={it.status} /></td>
                          <td className="p-2 text-right font-bold text-green-700">
                            {it.prize_amount > 0 ? `RD$${it.prize_amount.toFixed(2)}` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="px-6 pb-6 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Premio total</div>
                  <div className={`text-4xl font-black font-mono ${data.is_winner ? "text-green-700" : "text-slate-400"}`} data-testid="payout-total-prize">
                    RD${data.total_prize.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => printTicket(t, { isReprint: true })} className="h-12 rounded-sm border-slate-300" data-testid="payout-reprint">
                    <Printer className="w-4 h-4 mr-2" /> Reimprimir
                  </Button>
                  <Button
                    onClick={pay}
                    disabled={!data.can_pay || paying}
                    className="h-12 px-6 bg-green-600 hover:bg-green-700 text-white font-black rounded-sm disabled:bg-slate-300"
                    data-testid="payout-pay-btn"
                  >
                    {paying ? "Pagando…" : (
                      <><Banknote className="w-5 h-5 mr-2" /> Pagar ticket</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {paymentOk && (
            <div className="mt-4 bg-green-50 border-2 border-green-600 rounded-sm p-5 flex items-center gap-3" data-testid="payout-success">
              <CheckCircle2 className="w-8 h-8 text-green-700" />
              <div>
                <div className="font-black text-green-900">¡Pago realizado!</div>
                <div className="text-sm text-green-800 font-mono">
                  {paymentOk.ticket_number} · RD${paymentOk.amount.toFixed(2)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <QRScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDecode={handleScannedText}
      />
    </div>
  );
}

function StatusBanner({ data, t }) {
  if (t.status === "paid") {
    return (
      <div className="bg-slate-100 border-b-2 border-slate-300 px-6 py-3 flex items-center gap-2">
        <Ban className="w-4 h-4 text-slate-600" />
        <span className="text-sm font-bold text-slate-700">Este ticket ya fue pagado.</span>
      </div>
    );
  }
  if (t.status === "cancelled") {
    return (
      <div className="bg-red-50 border-b-2 border-red-300 px-6 py-3 flex items-center gap-2">
        <Ban className="w-4 h-4 text-red-700" />
        <span className="text-sm font-bold text-red-700">Ticket cancelado.</span>
      </div>
    );
  }
  if (data.has_pending_eval) {
    return (
      <div className="bg-yellow-50 border-b-2 border-yellow-400 px-6 py-3 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-yellow-700" />
        <span className="text-sm font-bold text-yellow-900">
          Algunas jugadas aún no evaluadas (falta el resultado del sorteo).
        </span>
      </div>
    );
  }
  if (data.is_winner) {
    return (
      <div className="bg-green-50 border-b-2 border-green-500 px-6 py-3 flex items-center gap-2">
        <Trophy className="w-5 h-5 text-green-700" />
        <span className="text-sm font-black uppercase tracking-wider text-green-800">
          ¡Ticket ganador!
        </span>
      </div>
    );
  }
  return (
    <div className="bg-slate-50 border-b-2 border-slate-200 px-6 py-3 flex items-center gap-2">
      <Ban className="w-4 h-4 text-slate-500" />
      <span className="text-sm font-semibold text-slate-600">Sin premios en este ticket.</span>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    active: "bg-blue-100 text-blue-700",
    paid: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
  };
  const label = { active: "Activo", paid: "Pagado", cancelled: "Cancelado" };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${map[s]}`}>{label[s]}</span>;
}

function ItemStatus({ s }) {
  const map = {
    pending: "bg-slate-100 text-slate-600",
    won: "bg-green-100 text-green-700",
    lost: "bg-slate-100 text-slate-400",
    paid: "bg-green-600 text-white",
  };
  const label = { pending: "Pendiente", won: "Ganador", lost: "Sin premio", paid: "Pagado" };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${map[s] || "bg-slate-100"}`}>{label[s] || s}</span>;
}

const Th = ({ children, className = "" }) => (
  <th className={`p-2 text-[10px] uppercase tracking-wider font-bold text-slate-600 ${className}`}>{children}</th>
);
